import React, { useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import {
    Box,
    FormControl,
    FormLabel,
    Input,
    Link,
    Stack,
    Typography,
    formLabelClasses,
    Button,
    Sheet,
} from "@mui/joy";
import { ColorSchemeToggle } from "../ColorSchemeToggle/index";

interface ForgotPasswordFormElements extends HTMLFormControlsCollection {
    email: HTMLInputElement;
}

interface ResetPasswordFormElements extends HTMLFormControlsCollection {
    code: HTMLInputElement;
    newPassword: HTMLInputElement;
    confirmPassword: HTMLInputElement;
}

interface ForgotPasswordFormElement extends HTMLFormElement {
    readonly elements: ForgotPasswordFormElements;
}

interface ResetPasswordFormElement extends HTMLFormElement {
    readonly elements: ResetPasswordFormElements;
}

export const ForgotPassword = () => {
    const [codeStep, setCodeStep] = useState(false);
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                background: (theme) =>
                    theme.palette.mode === "dark"
                        ? "radial-gradient(ellipse at top, rgba(66, 171, 155, 0.08) 0%, transparent 60%), radial-gradient(ellipse at bottom right, rgba(66, 171, 155, 0.05) 0%, transparent 50%)"
                        : "radial-gradient(ellipse at top, rgba(66, 171, 155, 0.12) 0%, transparent 60%), radial-gradient(ellipse at bottom right, rgba(230, 242, 239, 0.8) 0%, transparent 50%)",
            }}
        >
            {/* Header */}
            <Sheet
                sx={{
                    px: 3,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: "transparent",
                }}
            >
                <Typography
                    level="h4"
                    component={NavLink}
                    to="/"
                    sx={{
                        fontWeight: 800,
                        textDecoration: "none",
                        background: "linear-gradient(135deg, #42AB9B 0%, #328D7F 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                    }}
                >
                    🦩 Stork
                </Typography>
                <ColorSchemeToggle />
            </Sheet>

            {/* Main Content */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2,
                    py: 4,
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 420,
                        animation: "fadeInUp 0.5s ease-out",
                        "@keyframes fadeInUp": {
                            from: { opacity: 0, transform: "translateY(20px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                    }}
                >
                    <Sheet
                        variant="outlined"
                        sx={{
                            p: 4,
                            borderRadius: "xl",
                            boxShadow: "lg",
                            bgcolor: "background.surface",
                            [`& .${formLabelClasses.asterisk}`]: {
                                visibility: "hidden",
                            },
                        }}
                    >
                        {codeStep ? (
                            <Stack gap={4}>
                                <Stack gap={1} sx={{ textAlign: "center" }}>
                                    <Typography component="h1" level="h2" sx={{ fontWeight: 700 }}>
                                        Enter your code
                                    </Typography>
                                    <Typography level="body-md" textColor="text.secondary">
                                        We sent a reset code to your email
                                    </Typography>
                                </Stack>

                                {error && (
                                    <Typography level="body-sm" color="danger" sx={{ textAlign: "center" }}>
                                        {error}
                                    </Typography>
                                )}

                                <form
                                    onSubmit={async (event: React.FormEvent<ResetPasswordFormElement>) => {
                                        event.preventDefault();
                                        setError("");
                                        setIsLoading(true);
                                        const formElements = event.currentTarget.elements;

                                        try {
                                            await confirmResetPassword({
                                                confirmationCode: formElements.code.value,
                                                newPassword: formElements.confirmPassword.value,
                                                username: email,
                                            });
                                            navigate("/signin");
                                        } catch (error: any) {
                                            console.error("Error resetting password:", error);
                                            setError(error?.message || "Failed to reset password");
                                        } finally {
                                            setIsLoading(false);
                                        }
                                    }}
                                    style={{ display: "flex", flexDirection: "column", gap: "16px" }}
                                >
                                    <FormControl required>
                                        <FormLabel>Verification Code</FormLabel>
                                        <Input type="number" name="code" size="lg" placeholder="123456" />
                                    </FormControl>
                                    <FormControl required>
                                        <FormLabel>New Password</FormLabel>
                                        <Input type="password" name="newPassword" size="lg" />
                                    </FormControl>
                                    <FormControl required>
                                        <FormLabel>Confirm New Password</FormLabel>
                                        <Input type="password" name="confirmPassword" size="lg" />
                                    </FormControl>

                                    <Button type="submit" size="lg" loading={isLoading} sx={{ mt: 1 }}>
                                        Reset Password
                                    </Button>

                                    <Button
                                        type="button"
                                        variant="plain"
                                        color="neutral"
                                        onClick={async () => {
                                            try {
                                                await resetPassword({ username: email });
                                            } catch (error) {
                                                console.error("Error resending code:", error);
                                            }
                                        }}
                                    >
                                        Didn't receive a code? Resend
                                    </Button>
                                </form>
                            </Stack>
                        ) : (
                            <Stack gap={4}>
                                <Stack gap={1} sx={{ textAlign: "center" }}>
                                    <Typography component="h1" level="h2" sx={{ fontWeight: 700 }}>
                                        Forgot password?
                                    </Typography>
                                    <Typography level="body-md" textColor="text.secondary">
                                        No worries, we'll send you reset instructions
                                    </Typography>
                                </Stack>

                                {error && (
                                    <Typography level="body-sm" color="danger" sx={{ textAlign: "center" }}>
                                        {error}
                                    </Typography>
                                )}

                                <form
                                    onSubmit={async (event: React.FormEvent<ForgotPasswordFormElement>) => {
                                        event.preventDefault();
                                        setError("");
                                        setIsLoading(true);
                                        const formElements = event.currentTarget.elements;

                                        try {
                                            setEmail(formElements.email.value);
                                            await resetPassword({
                                                username: formElements.email.value,
                                            });
                                            setCodeStep(true);
                                        } catch (error: any) {
                                            console.error("Error sending code:", error);
                                            setError(error?.message || "Failed to send reset code");
                                        } finally {
                                            setIsLoading(false);
                                        }
                                    }}
                                    style={{ display: "flex", flexDirection: "column", gap: "16px" }}
                                >
                                    <FormControl required>
                                        <FormLabel>Email</FormLabel>
                                        <Input type="email" name="email" size="lg" />
                                    </FormControl>

                                    <Button type="submit" size="lg" loading={isLoading} sx={{ mt: 1 }}>
                                        Send Reset Code
                                    </Button>
                                </form>

                                <Typography level="body-sm" sx={{ textAlign: "center" }}>
                                    Remember your password?{" "}
                                    <Link component={NavLink} to="/signin" fontWeight="lg">
                                        Sign in
                                    </Link>
                                </Typography>
                            </Stack>
                        )}
                    </Sheet>
                </Box>
            </Box>
        </Box>
    );
};
